// =============================================================================
// features/contracts/ContractDetail.tsx — Drawer de ficha do contrato (F17-S05, F17-S06).
//
// Exibe todos os campos do ContractSchema.
// Botão "Marcar como assinado" visível apenas quando:
//   - status === 'draft'
//   - usuário tem permissão contracts:sign
//
// F17-S06: adiciona seção "Saúde" (ContractHealthBadge) e seção "Parcelas" (ContractDuesList).
//
// F46-S10: adiciona checklist + painel de documentos do contrato, e um gate
// opcional (flag `documents.contract_signature_gate.enabled`, OFF por
// padrão) que desabilita "Marcar como assinado" (draft → signed) enquanto a
// documentação obrigatória do contrato não estiver completa. A ativação
// (signed → active) NUNCA é gateada — é desembolso, não assinatura.
//
// `DocumentsPanel` (F46-S08) exige `leadId`, mas `ContractSchema` não expõe
// lead — só `customer_id`/`analysis_id`. Resolvido via `analysis_id` (quando
// presente): reusa GET /api/credit-analyses/:id, que já devolve `lead_id`
// (nenhuma rota nova, nenhum campo novo no contrato). Contratos sem
// `analysis_id` (legado/importados) ou ator sem `credit_analyses:read`
// simplesmente não resolvem `leadId` — o painel de documentos não é
// renderizado, mas o checklist (que resolve o lead sozinho no backend) e o
// gate de assinatura continuam funcionando normalmente.
//
// DS:
//   - Drawer lateral: elev-5, border, bg-elev-1 (DS §9 modal).
//   - Overlay translúcido à esquerda.
//   - Bricolage Grotesque para heading, Geist para labels, Mono para valores.
//   - Badge de status §9.5.
//   - Animação: slide da direita (translate-x).
//
// LGPD: exibe apenas dados do contrato — sem CPF do cliente.
// =============================================================================

import type { ChecklistResponse } from '@elemento/shared-schemas';
import * as React from 'react';

import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { useToast } from '../../components/ui/Toast';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useAuthStore } from '../../lib/auth-store';
import {
  DocumentChecklistHeader,
  sortChecklistItems,
} from '../credit-analyses/components/DocumentChecklistHeader';
import { useCreditAnalysis } from '../credit-analyses/hooks/useCreditAnalyses';
import { useDocumentChecklist } from '../credit-analyses/hooks/useDocumentChecklist';
import { DocumentsPanel } from '../documents';

import { ContractDuesList } from './ContractDuesList';
import { ContractHealthBadge } from './ContractHealthBadge';
import { ContractSignModal } from './ContractSignModal';
import { useContract, useSignContract } from './hooks';
import { CONTRACT_STATUS_META } from './schemas';

// F46-S10: flag do gate de assinatura — nasce OFF (fail-closed quando não
// presente no mapa de flags, ver hooks/useFeatureFlag.ts). Registro da chave
// no catálogo de flags do backend (apps/api/src/db/seeds/featureFlags.ts) é
// follow-up de backend — fora de files_allowed deste slot (frontend-only).
const CONTRACT_SIGNATURE_GATE_FLAG = 'documents.contract_signature_gate.enabled';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(value: string): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(parseFloat(value));
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Decide se "Marcar como assinado" (draft → signed) fica bloqueado pelo
 * gate de documentação. Puro — testado em __tests__/contract-documents.test.tsx
 * nos dois estados da flag (DoD: "Gate de assinatura respeita a flag e nasce
 * OFF").
 *
 * `checklistComplete === undefined` (dado ainda não carregado) NUNCA
 * bloqueia — evita travar o botão por uma corrida de loading; o gate só
 * bloqueia com um resultado definitivo de "incompleto".
 */
export function computeSignatureGateBlocking(params: {
  flagEnabled: boolean;
  isDraftSignTransition: boolean;
  checklistCanRead: boolean;
  checklistComplete: boolean | undefined;
}): boolean {
  return (
    params.flagEnabled &&
    params.isDraftSignTransition &&
    params.checklistCanRead &&
    params.checklistComplete !== undefined &&
    !params.checklistComplete
  );
}

// ---------------------------------------------------------------------------
// F46-S32: motivo visível do bloqueio de "Marcar como assinado"
//
// Pedido do Rogério (07/09/2026): um botão desabilitado sem explicação deixa
// o operador sem saber "se é permissão, se é bug, ou se falta algo dele
// fazer". Os dois textos abaixo são literais (definidos por ele) — não
// parafrasear.
//
// Duas causas possíveis, nunca uma terceira:
//   1. "documents_unavailable" — o app não tem como saber se a documentação
//      está completa: falta a flag `documents.enabled`/permissão
//      `documents:read` (checklistCanRead=false) OU o contrato não resolveu
//      `leadId` para o painel de documentos (sem `analysis_id`, sem
//      `credit_analyses:read`, ou erro ao buscar a análise). Neste segundo
//      caso o checklist até responde (ele resolve o lead sozinho via
//      `customers.primary_lead_id` no backend — ver checklist.service.ts),
//      mas a tela não pode dizer que a documentação está "reprovada" quando
//      o painel de ação (DocumentsPanel) nem aparece para o operador agir.
//   2. "documents_pending" — o gate está ativo e a checklist obrigatória
//      está mesmo incompleta. Lista os documentos que faltam (não apenas
//      "pendente" — dizer o quê falta resolve, "documentação pendente" só
//      move a dúvida um passo adiante).
// ---------------------------------------------------------------------------

export const SIGNATURE_BLOCK_MESSAGES = {
  documents_unavailable: 'A opção não está disponível no momento',
  documents_pending: 'A documentação ainda não foi aprovada',
} as const;

export interface SignatureBlockReason {
  kind: 'documents_unavailable' | 'documents_pending';
  message: string;
  missingLabels: string[];
}

/**
 * Decide o motivo visível do bloqueio — só chamada quando
 * `computeSignatureGateBlocking` já disse `true` (gate não muda de regra
 * aqui, só de explicação). Puro — testado em __tests__/contract-documents.test.tsx.
 */
export function computeSignatureBlockReason(params: {
  signatureGateBlocking: boolean;
  checklistCanRead: boolean;
  hasDocumentsContext: boolean;
  missingLabels: string[];
}): SignatureBlockReason | null {
  if (!params.signatureGateBlocking) return null;

  if (!params.checklistCanRead || !params.hasDocumentsContext) {
    return {
      kind: 'documents_unavailable',
      message: SIGNATURE_BLOCK_MESSAGES.documents_unavailable,
      missingLabels: [],
    };
  }

  return {
    kind: 'documents_pending',
    message: SIGNATURE_BLOCK_MESSAGES.documents_pending,
    missingLabels: params.missingLabels,
  };
}

/** Rótulos dos itens obrigatórios ainda não aprovados, na mesma ordem de
 * prioridade do `DocumentChecklistHeader` (pendências primeiro). */
export function getMissingDocumentLabels(checklistData: ChecklistResponse | undefined): string[] {
  if (!checklistData) return [];
  return sortChecklistItems(checklistData.required)
    .filter((item) => item.state !== 'approved')
    .map((item) => item.label);
}

/** Texto final exibido — inclui a lista de documentos faltantes quando houver. */
export function formatSignatureBlockReason(reason: SignatureBlockReason): string {
  if (reason.kind === 'documents_pending' && reason.missingLabels.length > 0) {
    return `${reason.message}. Falta: ${reason.missingLabels.join(', ')}.`;
  }
  return reason.message;
}

function formatDateOnly(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(`${dateStr}T00:00:00`).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// Campo de detalhe
// ---------------------------------------------------------------------------

function DetailRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div
      className="flex items-start justify-between gap-4 py-2.5"
      style={{ borderBottom: '1px solid var(--border-subtle)' }}
    >
      <span
        className="font-sans text-ink-3 shrink-0"
        style={{ fontSize: 'var(--text-xs)', paddingTop: 1 }}
      >
        {label}
      </span>
      <span className="font-sans text-ink text-right" style={{ fontSize: 'var(--text-sm)' }}>
        {children}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skeleton de carregamento
// ---------------------------------------------------------------------------

function DrawerSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-3 mt-6" aria-hidden="true">
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="h-5 rounded-xs animate-pulse"
          style={{
            width: `${50 + ((i * 13) % 40)}%`,
            background: 'var(--surface-muted)',
          }}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ContractDetailProps {
  contractId: string;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export function ContractDetail({ contractId, onClose }: ContractDetailProps): React.JSX.Element {
  const { toast } = useToast();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canSign = hasPermission('contracts:sign');

  const [showSignModal, setShowSignModal] = React.useState(false);

  const { data: contract, isLoading, isError, refetch } = useContract(contractId);
  const { mutate: signContract, isPending: isSigning } = useSignContract();

  // ─── F46-S10: documentos ──────────────────────────────────────────────────

  // leadId não vem no ContractSchema — resolvido via analysis_id (quando
  // presente) reusando GET /api/credit-analyses/:id (nenhuma rota/campo
  // novo). Ver nota de arquitetura no topo do arquivo.
  const canReadAnalysis = hasPermission('credit_analyses:read');
  const analysisId = contract?.analysis_id ?? null;
  const { data: linkedAnalysis, isError: isAnalysisError } = useCreditAnalysis(
    canReadAnalysis && analysisId ? analysisId : '',
  );
  const leadIdForDocuments =
    canReadAnalysis && analysisId && !isAnalysisError ? (linkedAnalysis?.lead_id ?? null) : null;

  const checklist = useDocumentChecklist('contract', contract?.id ?? '');
  const signatureGate = useFeatureFlag(CONTRACT_SIGNATURE_GATE_FLAG);

  // Gate se aplica SÓ à transição draft → signed (assinatura). A ativação
  // (signed → active) é desembolso — nunca gateada por documentação.
  const isDraftSignTransition = contract?.status === 'draft';
  const signatureGateBlocking = computeSignatureGateBlocking({
    flagEnabled: signatureGate.enabled,
    isDraftSignTransition,
    checklistCanRead: checklist.canRead,
    checklistComplete: checklist.data?.summary.complete,
  });
  // F46-S32: motivo visível do bloqueio (não só title/hover — a operação
  // usa celular, e hover não existe no toque).
  const missingDocumentLabels = React.useMemo(
    () => getMissingDocumentLabels(checklist.data),
    [checklist.data],
  );
  const signatureBlockReason = computeSignatureBlockReason({
    signatureGateBlocking,
    checklistCanRead: checklist.canRead,
    hasDocumentsContext: leadIdForDocuments !== null,
    missingLabels: missingDocumentLabels,
  });
  const signatureGateReasonText = signatureBlockReason
    ? formatSignatureBlockReason(signatureBlockReason)
    : null;

  // Fechar com Escape (quando o modal de assinatura não estiver aberto)
  React.useEffect(() => {
    function handleKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape' && !showSignModal) onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showSignModal, onClose]);

  const handleSign = (): void => {
    if (!contract) return;
    signContract(
      { id: contract.id },
      {
        onSuccess: () => {
          toast('Contrato marcado como assinado', 'success');
          setShowSignModal(false);
        },
        onError: (err) => {
          toast(`Erro ao assinar: ${err.message}`, 'danger');
          setShowSignModal(false);
        },
      },
    );
  };

  const statusMeta = contract
    ? (CONTRACT_STATUS_META[contract.status] ?? {
        label: contract.status,
        variant: 'neutral' as const,
      })
    : null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-40"
        style={{ background: 'color-mix(in srgb, var(--text) 40%, transparent)' }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        role="complementary"
        aria-label="Detalhes do contrato"
        className="fixed inset-y-0 right-0 z-40 flex flex-col w-full max-w-md"
        style={{
          background: 'var(--bg-elev-1)',
          boxShadow: 'var(--elev-5)',
          borderLeft: '1px solid var(--border)',
          animation: 'slide-in-right 250ms var(--ease-out) both',
        }}
      >
        {/* Header do drawer */}
        <div
          className="flex items-center justify-between px-6 py-4 shrink-0"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <h2
            className="font-display font-bold text-ink"
            style={{ fontSize: 'var(--text-lg)', letterSpacing: '-0.03em' }}
          >
            Contrato
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-sm text-ink-3 hover:text-ink hover:bg-surface-hover transition-colors"
            aria-label="Fechar detalhes"
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              className="w-4 h-4"
              aria-hidden="true"
            >
              <path d="M4 4l8 8M12 4l-8 8" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Conteúdo */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading && <DrawerSkeleton />}

          {!isLoading && isError && (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <p className="font-sans text-ink-3" style={{ fontSize: 'var(--text-sm)' }}>
                Erro ao carregar o contrato.
              </p>
              <Button variant="outline" size="sm" onClick={() => void refetch()}>
                Tentar novamente
              </Button>
            </div>
          )}

          {!isLoading && !isError && contract && (
            <div className="flex flex-col gap-0">
              {/* Badge de status em destaque */}
              <div className="flex items-center gap-2 mb-4">
                {statusMeta && <Badge variant={statusMeta.variant}>{statusMeta.label}</Badge>}
                <span
                  className="font-mono font-semibold text-azul"
                  style={{ fontSize: 'var(--text-base)' }}
                >
                  {contract.contract_reference}
                </span>
              </div>

              {/* Seção: Valores */}
              <p
                className="font-sans font-semibold uppercase text-ink-3 tracking-[0.08em] mb-1 mt-2"
                style={{ fontSize: '0.68rem' }}
              >
                Valores
              </p>

              <DetailRow label="Valor principal">
                <span
                  className="font-mono font-bold text-ink"
                  style={{ fontSize: 'var(--text-base)' }}
                >
                  {formatCurrency(contract.principal_amount)}
                </span>
              </DetailRow>

              <DetailRow label="Prazo">
                <span className="font-mono text-ink-2">
                  {contract.term_months} {contract.term_months === 1 ? 'mês' : 'meses'}
                </span>
              </DetailRow>

              {contract.monthly_rate_snapshot && (
                <DetailRow label="Taxa mensal">
                  <span className="font-mono text-ink-2">
                    {(parseFloat(contract.monthly_rate_snapshot) * 100).toFixed(4)}%
                  </span>
                </DetailRow>
              )}

              {/* Seção: Datas */}
              <p
                className="font-sans font-semibold uppercase text-ink-3 tracking-[0.08em] mb-1 mt-4"
                style={{ fontSize: '0.68rem' }}
              >
                Datas
              </p>

              <DetailRow label="Criado em">
                <span className="font-mono text-ink-2">{formatDate(contract.created_at)}</span>
              </DetailRow>

              {contract.signed_at && (
                <DetailRow label="Assinado em">
                  <span className="font-mono text-ink-2">{formatDate(contract.signed_at)}</span>
                </DetailRow>
              )}

              <DetailRow label="1ª parcela">
                <span className="font-mono text-ink-2">
                  {formatDateOnly(contract.first_due_date)}
                </span>
              </DetailRow>

              <DetailRow label="Última parcela">
                <span className="font-mono text-ink-2">
                  {formatDateOnly(contract.last_due_date)}
                </span>
              </DetailRow>

              <DetailRow label="Atualizado em">
                <span className="font-mono text-ink-2">{formatDate(contract.updated_at)}</span>
              </DetailRow>

              {/* Seção: Identificadores */}
              <p
                className="font-sans font-semibold uppercase text-ink-3 tracking-[0.08em] mb-1 mt-4"
                style={{ fontSize: '0.68rem' }}
              >
                Identificadores
              </p>

              <DetailRow label="ID interno">
                <span
                  className="font-mono text-ink-3"
                  style={{ fontSize: 'var(--text-xs)', wordBreak: 'break-all' }}
                >
                  {contract.id}
                </span>
              </DetailRow>

              {/* Seção: Saúde (F17-S06) */}
              <p
                className="font-sans font-semibold uppercase text-ink-3 tracking-[0.08em] mb-1 mt-4"
                style={{ fontSize: '0.68rem' }}
              >
                Saúde
              </p>
              <ContractHealthBadge contractId={contract.id} />

              {/* Seção: Documentos (F46-S10) — antes das Parcelas (F49-S02):
                  documento é insumo da decisão, e a lista de parcelas cresce
                  com o prazo (60 meses = 60 linhas) e empurraria os
                  documentos para fora da vista se viesse depois. Sem
                  `leadIdForDocuments` (contrato sem análise vinculada), nada
                  renderiza aqui — nenhum buraco visível. */}
              <p
                className="font-sans font-semibold uppercase text-ink-3 tracking-[0.08em] mb-2 mt-4"
                style={{ fontSize: '0.68rem' }}
              >
                Documentos
              </p>
              <div className="flex flex-col gap-3">
                <DocumentChecklistHeader flow="contract" contextId={contract.id} />
                {leadIdForDocuments && (
                  <DocumentsPanel
                    leadId={leadIdForDocuments}
                    scope="contract"
                    contextId={contract.id}
                  />
                )}
              </div>

              {/* Seção: Parcelas (F17-S06) */}
              <p
                className="font-sans font-semibold uppercase text-ink-3 tracking-[0.08em] mb-1 mt-4"
                style={{ fontSize: '0.68rem' }}
              >
                Parcelas
              </p>
              <ContractDuesList
                customerId={contract.customer_id}
                contractReference={contract.contract_reference}
                // Habilitam a ação "Gerar parcelas" quando a lista está vazia.
                contractId={contract.id}
                contractStatus={contract.status}
                termMonths={contract.term_months}
              />
            </div>
          )}
        </div>

        {/* Footer com ação de assinatura */}
        {!isLoading && !isError && contract?.status === 'draft' && canSign && (
          <div
            className="shrink-0 px-6 py-4"
            style={{ borderTop: '1px solid var(--border)', background: 'var(--bg-elev-2)' }}
          >
            <Button
              variant="primary"
              onClick={() => setShowSignModal(true)}
              disabled={isSigning || signatureGateBlocking}
              aria-describedby={signatureGateReasonText ? 'contract-sign-block-reason' : undefined}
              className="w-full justify-center"
              leftIcon={
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.6}
                  className="w-4 h-4"
                  aria-hidden="true"
                >
                  <path d="M2 12l3-1 7-7-2-2-7 7-1 3Z" strokeLinejoin="round" />
                  <path d="M12 2l2 2" strokeLinecap="round" />
                </svg>
              }
            >
              Marcar como assinado
            </Button>
            {signatureBlockReason && (
              // Motivo visível (nunca só title/hover — hover não existe no
              // toque). "documents_pending" usa --warning (falta um passo,
              // não é erro — nunca --danger aqui); "documents_unavailable"
              // usa --ink-2 neutro. Contraste medido, não presumido:
              // --warning (#7A6300) sobre --bg-elev-2 (#FBF8F1) ≈ 5,5:1 AA;
              // --text-2/ink-2 (#3C4A6B) sobre a mesma superfície ≈ 8,3:1 AA
              // — nota: --text-3/ink-3 (#6B7896), a escolha "óbvia" para
              // texto secundário, mede ≈ 4,17:1 aqui e REPROVA AA (mín.
              // 4,5:1) para texto pequeno — por isso ink-2, não ink-3.
              <p
                id="contract-sign-block-reason"
                className={
                  signatureBlockReason.kind === 'documents_pending'
                    ? 'font-sans text-center mt-2'
                    : 'font-sans text-center mt-2 text-ink-2'
                }
                style={{
                  fontSize: 'var(--text-xs)',
                  ...(signatureBlockReason.kind === 'documents_pending'
                    ? { color: 'var(--warning)' }
                    : {}),
                }}
                role="status"
              >
                {signatureGateReasonText}
              </p>
            )}
          </div>
        )}

        {/* Ativação (signed → active)
            Sem esta ação o fluxo terminava em "assinado" e o contrato nunca
            chegava a ativo — que é quando as parcelas são geradas. O operador
            marcava como assinado e ficava sem parcela e sem botão nenhum. */}
        {!isLoading && !isError && contract?.status === 'signed' && canSign && (
          <div
            className="shrink-0 px-6 py-4 flex flex-col gap-2"
            style={{ borderTop: '1px solid var(--border)', background: 'var(--bg-elev-2)' }}
          >
            <Button
              variant="primary"
              onClick={() => setShowSignModal(true)}
              disabled={isSigning}
              className="w-full justify-center"
              leftIcon={
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.6}
                  className="w-4 h-4"
                  aria-hidden="true"
                >
                  <path d="M3 8.5l3.5 3.5L13 5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              }
            >
              {isSigning ? 'Ativando…' : 'Ativar contrato'}
            </Button>
            <p className="font-sans text-ink-3 text-center" style={{ fontSize: 'var(--text-xs)' }}>
              Confirma o desembolso e gera as {contract.term_months} parcelas.
            </p>
          </div>
        )}
      </aside>

      {/* Modal de confirmação de assinatura */}
      {showSignModal && contract && (
        <ContractSignModal
          contract={contract}
          onConfirm={handleSign}
          onCancel={() => setShowSignModal(false)}
          isPending={isSigning}
          blockedReason={signatureGateReasonText}
          blockedReasonKind={
            signatureBlockReason?.kind === 'documents_unavailable' ? 'unavailable' : 'pending'
          }
        />
      )}
    </>
  );
}
